/**
 * gameConstants.ts
 * Global game tuning constants for Starfall Courier.
 * Physics-specific constants (GRAVITY, JUMP_FORCE, etc.) will live in
 * src/game/engine/physics.ts once the engine is built (Phase 1).
 * These constants govern gameplay rules, UI sizing, progression gating,
 * viewport dimensions, and the fixed-timestep target.
 *
 * Phase 0 stub — consumed by app/ screens and src/game/engine/ (Phase 1).
 * See ROADMAP.md Phase 0 and Phase 1.
 *
 * @module gameConstants
 */

// ─── World / Progression ────────────────────────────────────────────────────

/**
 * Canonical tuple of all playable world identifiers — the single source of truth.
 * `WorldId` is derived from this tuple so they cannot drift: adding or removing
 * an entry here automatically widens or narrows the `WorldId` union.
 */
export const WORLD_IDS = [1, 2, 3, 4, 5] as const;

/**
 * Union of all valid world identifiers.
 * Derived from `WORLD_IDS` — do not maintain this separately.
 */
export type WorldId = (typeof WORLD_IDS)[number];

/**
 * Total number of playable worlds.
 * Derived from `WORLD_IDS` so it always stays in sync.
 */
export const WORLD_COUNT = WORLD_IDS.length;

/** Number of Star Seeds collected before a milestone bark triggers. */
export const SEED_MILESTONE_INTERVAL = 50;

/** Score awarded per Star Seed collected. */
export const SEED_SCORE = 10;

/** Score awarded per Constellation Shard collected. */
export const SHARD_SCORE = 500;

/** Score awarded per Heart Fragment collected. */
export const HEART_FRAGMENT_SCORE = 250;

/**
 * HP increase per Heart Fragment. Two fragments = one full heart.
 * Persisted via SaveManager as collected fragment ids and re-applied to
 * maxHealth on every level load — see `src/game/data/heartUpgrades.ts`, which
 * floors the total so max HP is always a whole number of hearts.
 */
export const HEART_FRAGMENT_HP_VALUE = 0.5;

// ─── Nova ────────────────────────────────────────────────────────────────────

/** Nova's maximum hit points. */
export const NOVA_MAX_HP = 3;

/** Starting lives per game session. */
export const NOVA_LIVES_START = 3;

/** Invincibility frames after taking damage (at 60fps). */
export const NOVA_INVINCIBILITY_FRAMES = 90;

/**
 * Frames of invincibility during which the HURT animation is forced.
 * After this window, normal movement animations play while blink continues.
 */
export const NOVA_HURT_ANIM_FRAMES = 15;

/** Sprite dimensions in logical pixels. */
export const NOVA_WIDTH = 29;
export const NOVA_HEIGHT = 43;

/**
 * Pixels to inset the combat hitbox from the top of the physics AABB.
 * The physics box (NOVA_HEIGHT = 43) is taller than the visible sprite
 * content (~30 px). Without this offset, enemies trigger damage 13 px
 * above Nova's visible head. Value derived from the normalised sprite:
 * content starts at row 18 of the 60 px frame → 18 × (65 / 60) ≈ 19.5 px
 * into the 65 px draw area, which maps to nova.y + 13.75 in world space.
 */
export const NOVA_COMBAT_TOP_INSET = 13;

/**
 * Additional top inset (px) applied to the combat hitbox while crouched.
 * Standing visible height ≈ 30 px; crouched visible height ≈ 16 px, so the
 * head drops ~14 px below the standing-pose head position. Adding this on
 * top of `NOVA_COMBAT_TOP_INSET` keeps flying enemies (e.g. Gloombat) from
 * hitting the player when their sprite has clearly ducked beneath the path.
 * Applies to all playable characters since the physics box is shared.
 */
export const NOVA_COMBAT_CROUCH_EXTRA_TOP_INSET = 14;

/** Maximum horizontal speed while crouching (px/s). */
export const CROUCH_MAX_SPEED = 60;

/**
 * Height (px) of Nova's physics collider while crouched.
 *
 * DECISION: Derived from {@link NOVA_COMBAT_CROUCH_EXTRA_TOP_INSET} rather than
 * picked by feel. That constant already measures how far the head drops between
 * the standing and crouched poses, so reusing it keeps the physics collider and
 * the combat hitbox describing the same silhouette — if the crouch art is
 * retimed, both follow together instead of drifting apart.
 *
 * The resulting 29px clears a two-tile (32px) gap, which is what makes crouch
 * a traversal move rather than just a speed cap.
 */
export const NOVA_CROUCH_HEIGHT = NOVA_HEIGHT - NOVA_COMBAT_CROUCH_EXTRA_TOP_INSET;

/**
 * Maximum descent speed (px/s) while wall sliding.
 * A fraction of `MAX_FALL_SPEED` (900) so a slide reads as a controlled
 * descent the player can react from, not a slightly slower fall.
 */
export const WALL_SLIDE_MAX_FALL_SPEED = 120;

/**
 * Horizontal probe distance (px) used to detect a wall beside Nova.
 * Large enough to survive the sub-pixel gap collision resolution leaves behind,
 * small enough that it can't detect a wall she is not touching.
 */
export const WALL_PROBE_DISTANCE = 2;

/** Horizontal speed (px/s) held for the duration of a dash. */
export const DASH_SPEED = 560;

/** Dash duration in frames (~0.17s at 60Hz, covering roughly 93px). */
export const DASH_DURATION_FRAMES = 10;

/** Frames after a dash ends before another can start. */
export const DASH_COOLDOWN_FRAMES = 24;

/** Coyote-time window in frames (allows jump after walking off ledge). */
export const COYOTE_FRAMES = 8;

/** Jump buffer window in frames (queues a jump before landing). */
export const JUMP_BUFFER_FRAMES = 6;

// ─── Projectiles ────────────────────────────────────────────────────────────

/** Star Shard: maximum active projectiles in the pool. */
export const MAX_PROJECTILES = 50;

/** Star Shard: shards per attack press. */
export const PROJECTILE_BURST = 1;

/** Star Shard: cooldown between bursts in seconds. */
export const PROJECTILE_COOLDOWN_S = 0.5;

// ─── Visual Effects ──────────────────────────────────────────────────────────

// ── Animation transition durations (physics frames at 60fps) ─────────────

/** Frames to hold RUN_START (dust push-off) before entering RUN loop. */
export const RUN_START_FRAMES = 8;

/** Frames to hold RUN_STOP (braking dust) before returning to IDLE. */
export const RUN_STOP_FRAMES = 10;

/** Frames to hold LAND (landing dust) before switching to IDLE or RUN. */
export const LAND_FRAMES = 8;

// ─── Particles ───────────────────────────────────────────────────────────────

/** Maximum active particles in the pool (all effects combined). */
export const MAX_PARTICLES = 50;

// ─── UI / Touch ──────────────────────────────────────────────────────────────

/** Minimum touch target dimension in logical points (accessibility requirement). */
export const MIN_TOUCH_PT = 60;

/** HUD top-safe-area padding in logical points. */
export const HUD_SAFE_TOP = 12;

// ─── Camera ──────────────────────────────────────────────────────────────────

/** How far ahead of Nova the camera looks in the direction of travel (logical px). */
export const CAMERA_LOOK_AHEAD = 80;

/** Camera lerp factor (0–1). Lower = smoother but laggier. */
export const CAMERA_LERP = 0.12;

// ─── Tile / Viewport ─────────────────────────────────────────────────────────

/** Tile size in logical pixels (must match Tiled map export). */
export const TILE_SIZE = 16;

/** Logical game resolution width (viewport). */
export const VIEWPORT_WIDTH = 480;

/** Logical game resolution height (viewport). */
export const VIEWPORT_HEIGHT = 270;

// ─── Frame Rate ───────────────────────────────────────────────────────────────

/** Target frame rate. Reanimated useFrameCallback targets the display refresh
 *  rate; this value is used for physics fixed-step calculations and
 *  frame-count-based timers (Phase 1). */
export const TARGET_FPS = 60;

/** Fixed physics timestep in seconds (1 / TARGET_FPS). */
export const FIXED_DT = 1 / TARGET_FPS;
